import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Urls, Titles } from '@/lib/navigation';
import { BackButton } from './BackButton';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { trackDemoLaunched } from '@/analytics';
import { useOptionalVmClient, useVmConnection } from '@/api/context';
import { useAppSession } from '@/contexts/SessionContext';
import type { DemoManifestEntry, DemoPreviewHoverGradientTheme } from '@/api/types';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_DISPLAY_CLASS,
  PAPER_ICON_SMALL_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from './paperShapes';

// Token values mirror the corresponding `--surface` / `--fg-2` slots in
// globals.css's `.theme-paper` (dark) and `.theme-paper.theme-paper-light`.
// Kept inline so the overlay can render a fixed tone without depending on
// the active app theme.
const PREVIEW_OVERLAY_TONES = {
  dark: { surface: '#1a1916', text: '#EDEAE0' },
  light: { surface: '#F5F3E7', text: '#1E1E1E' },
} as const;

const resolvePreviewOverlayStyle = (
  theme: DemoPreviewHoverGradientTheme | null | undefined,
): { background: string; color: string } => {
  // Default to 'dark' when the manifest omits a value: most demo thumbnails
  // are captured in dark mode, so a dark overlay is the safer default.
  const resolved: DemoPreviewHoverGradientTheme = theme ?? 'dark';
  if (resolved === 'auto') {
    return {
      background:
        'linear-gradient(to top, color-mix(in srgb, var(--surface) 96%, transparent) 0%, color-mix(in srgb, var(--surface) 90%, transparent) 60%, color-mix(in srgb, var(--surface) 0%, transparent) 100%)',
      color: 'var(--fg-2)',
    };
  }
  const { surface, text } = PREVIEW_OVERLAY_TONES[resolved];
  return {
    background: `linear-gradient(to top, color-mix(in srgb, ${surface} 96%, transparent) 0%, color-mix(in srgb, ${surface} 90%, transparent) 60%, color-mix(in srgb, ${surface} 0%, transparent) 100%)`,
    color: text,
  };
};

const PASTEL_BG = [
  'bg-[#dbe7d8]',
  'bg-[#f1e2c0]',
  'bg-[#ece4f4]',
  'bg-[#f3dcd3]',
  'bg-[#ebe9e4]',
  'bg-[#e2dcf2]',
];

const sortDemosForMobile = (demos: DemoManifestEntry[]): DemoManifestEntry[] => {
  const ordered: DemoManifestEntry[] = [];
  const unordered: DemoManifestEntry[] = [];
  for (const demo of demos) {
    if (demo.mobile_order != null) ordered.push(demo);
    else unordered.push(demo);
  }
  ordered.sort((a, b) => a.mobile_order! - b.mobile_order!);
  return [...ordered, ...unordered];
};

const PaperDemoTile = memo(function PaperDemoTile({
  demo,
  index,
}: {
  demo: DemoManifestEntry;
  index: number;
}) {
  const vmClient = useOptionalVmClient();
  const { actions } = useAppSession();
  const [imgFailed, setImgFailed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [hover, setHover] = useState(false);

  const handleClick = useCallback(async () => {
    if (!vmClient || activating) return;
    setActivating(true);
    trackDemoLaunched(demo.name);
    try {
      const sessionId = await vmClient.activateDemo(demo.name);
      await actions.handleSelectAgent(sessionId);
    } catch {
      setActivating(false);
    }
  }, [vmClient, demo.name, actions, activating]);

  if (!vmClient) return null;

  const thumbnailSrc = demo.thumbnail_url ? vmClient.getThumbnailUrl(demo.thumbnail_url) : null;
  const showImage = thumbnailSrc && !imgFailed;
  const placeholder = PASTEL_BG[index % PASTEL_BG.length];

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={activating}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[14px] text-left cursor-pointer',
        'bg-[var(--surface)] border border-solid border-[var(--hair)]',
        'transition-all duration-200 ease-[cubic-bezier(.22,.61,.36,1)]',
        hover && !activating && 'border-[var(--periwinkle)] -translate-y-0.5 shadow-[0_12px_28px_rgba(20,18,14,0.06)]',
        activating && 'border-glow-busy pointer-events-none',
      )}
      style={{ ['--border-glow-fill' as string]: 'var(--surface)' }}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        {showImage ? (
          <img
            src={thumbnailSrc}
            alt={demo.title}
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <div className={cn('flex h-full w-full items-center justify-center', placeholder)}>
            <Sparkles
              className={cn(PAPER_ICON_DISPLAY_CLASS, 'text-[var(--fg-4)]')}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
          </div>
        )}

        {demo.description && (() => {
          const overlay = resolvePreviewOverlayStyle(demo.preview_hover_gradient_theme);
          return (
            <div
              className={cn(
                'absolute inset-x-0 bottom-[-1px] px-[18px] pb-[17px] pt-5',
                'transition-transform duration-[320ms] ease-[cubic-bezier(.22,.61,.36,1)]',
                hover ? 'translate-y-0' : 'translate-y-[101%]',
              )}
              style={{ background: overlay.background }}
            >
              <p
                className="text-[12.5px] leading-[1.55]"
                style={{ color: overlay.color }}
              >
                {demo.description}
              </p>
            </div>
          );
        })()}
      </div>

      <div className="flex items-baseline justify-between gap-[10px] border-t border-solid border-[var(--hair)] bg-[var(--surface)] px-[18px] py-[14px]">
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-[17px] leading-[1.15] tracking-[-0.01em] text-[var(--fg)]"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 400 }}
          >
            {demo.title}
          </div>
          {demo.tags && demo.tags.length > 0 && (
            <div className="mt-[6px] truncate text-[10px] uppercase tracking-[0.14em] text-[var(--fg-3)]">
              {demo.tags.join(' · ')}
            </div>
          )}
        </div>
        {activating && (
          <Loader2
            className={cn(PAPER_ICON_SMALL_CLASS, 'shrink-0 animate-spin text-[var(--fg-3)]')}
            strokeWidth={PAPER_ICON_STROKE_WIDTH}
          />
        )}
      </div>
    </button>
  );
});

const PaperDemoTileSkeleton = memo(function PaperDemoTileSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-[14px] border border-solid border-[var(--hair)] bg-[var(--surface)]">
      <div className="aspect-[16/10] w-full bg-[var(--bg-alt)]" />
      <div className="flex items-baseline justify-between gap-[10px] border-t border-solid border-[var(--hair)] px-[18px] py-[14px]">
        <div className="min-w-0 flex-1">
          <div className="h-[20px] w-3/4 rounded bg-[var(--bg-alt)]" />
          <div className="mt-[8px] h-[10px] w-1/2 rounded bg-[var(--bg-alt)]" />
        </div>
      </div>
    </div>
  );
});

export const PaperDemosPage = () => {
  useDocumentTitle(Titles.demos());
  const navigate = useNavigate();
  const vmClient = useOptionalVmClient();
  const vmConnection = useVmConnection();
  const isMobileDevice = useIsMobileDevice();
  const [demos, setDemos] = useState<DemoManifestEntry[]>([]);

  const displayedDemos = useMemo(
    () => (isMobileDevice ? sortDemosForMobile(demos) : demos),
    [demos, isMobileDevice],
  );

  useEffect(() => {
    if (!vmClient) {
      setDemos([]);
      return;
    }
    let cancelled = false;
    vmClient
      .fetchDemoManifest()
      .then((manifest) => {
        if (!cancelled) setDemos(manifest);
      })
      .catch(() => {
        if (!cancelled) setDemos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vmClient, vmConnection.status]);

  return (
    <main
      className="relative flex flex-1 flex-col overflow-y-auto bg-[var(--bg)] text-[var(--fg)]"
      style={{ fontFamily: 'var(--font-sans-paper)' }}
    >
      <div className="flex flex-1 flex-col min-w-0">
        {/* topbar */}
        <div
          className={cn(
            'flex shrink-0 items-center px-[28px]',
            'border-b border-solid border-[var(--hair)]',
          )}
          style={{ height: 56 }}
        >
          <BackButton />
        </div>

        {/* content */}
        <div className="flex-1 flex flex-col min-h-0 px-[40px] desktop:px-[80px] pt-[40px] pb-[40px]">
          <div className="mb-[32px] flex items-center justify-between gap-[16px]">
            <h1
              className="m-0 text-[clamp(36px,4.4vw,52px)] leading-[1.05] text-[var(--fg)]"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.018em', fontWeight: 400 }}
            >
              Get started in one click.
            </h1>
            <button
              type="button"
              onClick={() => navigate(Urls.home())}
              className={cn(
                'inline-flex shrink-0 items-center gap-[6px] h-[30px] px-[14px]',
                PAPER_CONTROL_RADIUS_CLASS,
                'text-[11px] font-medium tracking-[0.02em] cursor-pointer',
                'bg-[var(--fg)] text-[var(--fg-inverse)] border border-solid border-[var(--fg)]',
                'transition-colors duration-200',
                'hover:bg-[var(--ink-3)] hover:border-[var(--ink-3)]',
              )}
            >
              <Plus className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              NEW AGENT
            </button>
          </div>

          <div className="grid grid-cols-1 desktop:grid-cols-3 gap-[20px]">
            {displayedDemos.length === 0
              ? Array.from({ length: 6 }).map((_, i) => <PaperDemoTileSkeleton key={i} />)
              : displayedDemos.map((demo, i) => (
                  <PaperDemoTile key={demo.id} demo={demo} index={i} />
                ))}
          </div>
        </div>
      </div>
    </main>
  );
};
